import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  OrganizationService,
  PropertyService,
  UnitService,
  TenantService,
  ContractorService,
  PaymentService,
} from "@propertelligent/client-api";
import apiHelper from "../../../utils/apiHelper";
import RequestBreadcrumb from "../../common/RequestBreadcrumb";
import DynamicTabs from "../../../newComponents/tabs/DynamicTabs";
import SimpleSpinner from "../../loader";
import OrgScopedList from "./OrgScopedList";
import OrgSubscriptionTab from "./OrgSubscriptionTab";
import { URLS } from "../../../constants/constants";
import "../platform-portal.css";

const OrganizationDetail = () => {
  const { orgId } = useParams();
  const organizationId = Number(orgId);
  const [org, setOrg] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await apiHelper(
        OrganizationService.getApiOrganization1,
        { showNotification: false },
        organizationId
      );
      setOrg(res?.data?.success ?? null);
      setLoading(false);
    };
    load();
  }, [organizationId]);

  const overviewTab = (
    <div className="platform-tab-section">
      <div className="platform-info-card">
        <div className="platform-overview-grid">
          <div className="platform-overview-item">
            <label>Organization Name</label>
            <p className="normal_text black m-0">{org?.name || "—"}</p>
          </div>
          <div className="platform-overview-item">
            <label>Email</label>
            <p className="normal_text black m-0">{org?.email || "—"}</p>
          </div>
          <div className="platform-overview-item">
            <label>Support Email</label>
            <p className="normal_text black m-0">{org?.supportEmail || "—"}</p>
          </div>
          <div className="platform-overview-item">
            <label>Phone</label>
            <p className="normal_text black m-0">
              {org?.formatted?.PhoneNumber || org?.phoneNumber || "—"}
            </p>
          </div>
          <div className="platform-overview-item">
            <label>Website</label>
            <p className="normal_text black m-0">{org?.website || "—"}</p>
          </div>
          <div className="platform-overview-item">
            <label>Address</label>
            <p className="normal_text black m-0">
              {org?.address?.streetAddress
                ? `${org.address.streetAddress}, ${org.address.city || ""} ${org.address.state || ""} ${org.address.zip || ""}`
                : "—"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: "overview", label: "Overview", content: overviewTab, onClick: () => {} },
    {
      id: "subscription",
      label: "Subscription",
      content: <OrgSubscriptionTab organizationId={organizationId} />,
      onClick: () => {},
    },
    {
      id: "properties",
      label: "Properties",
      content: (
        <OrgScopedList
          organizationId={organizationId}
          httpFetchDataFn={PropertyService.getApiPropertyOrganizationlist}
          emptyHeading="No properties"
          columns={[
            { key: "streetAddress", label: "Address" },
            { key: "city", label: "City" },
            { key: "propertyType", label: "Type" },
            { key: "numberOfUnits", label: "Units" },
            { key: "vacantUnits", label: "Vacant" },
            { key: "openServiceRequests", label: "Open SR" },
          ]}
        />
      ),
      onClick: () => {},
    },
    {
      id: "units",
      label: "Units",
      content: (
        <OrgScopedList
          organizationId={organizationId}
          httpFetchDataFn={UnitService.getApiUnitOrganizationlist}
          emptyHeading="No units"
          columns={[
            { key: "unitNumber", label: "Unit" },
            { key: "streetAddress", label: "Address" },
            { key: "numberOfBedrooms", label: "Beds" },
            { key: "numberOfBathrooms", label: "Baths" },
            { key: "rent", label: "Rent" },
            { key: "leaseDisplayText", label: "Lease" },
          ]}
        />
      ),
      onClick: () => {},
    },
    {
      id: "tenants",
      label: "Tenants",
      content: (
        <OrgScopedList
          organizationId={organizationId}
          httpFetchDataFn={TenantService.getApiTenantOrganizationlist}
          emptyHeading="No tenants"
          columns={[
            { key: "fullName", label: "Name", render: (r) => r?.fullName || `${r?.firstName || ""} ${r?.lastName || ""}`.trim() || "—" },
            { key: "email", label: "Email" },
            { key: "phoneNumber", label: "Phone" },
            { key: "unitNumber", label: "Unit" },
            { key: "leaseDisplayText", label: "Lease" },
          ]}
        />
      ),
      onClick: () => {},
    },
    {
      id: "contractors",
      label: "Contractors",
      content: (
        <OrgScopedList
          organizationId={organizationId}
          httpFetchDataFn={ContractorService.getApiContractorOrganizationlist}
          emptyHeading="No contractors"
          columns={[
            { key: "fullName", label: "Name", render: (r) => r?.fullName || `${r?.firstName || ""} ${r?.lastName || ""}`.trim() || "—" },
            { key: "email", label: "Email" },
            { key: "phoneNumber", label: "Phone" },
            { key: "openServiceRequestCount", label: "Open SR" },
          ]}
        />
      ),
      onClick: () => {},
    },
    {
      id: "payments",
      label: "Payments",
      content: (
        <OrgScopedList
          organizationId={organizationId}
          httpFetchDataFn={PaymentService.getApiPaymentDueAccountOrganizationlist}
          emptyHeading="No payment due accounts"
          columns={[
            { key: "payerPayeeName", label: "Payer / Payee" },
            { key: "description", label: "Description" },
            { key: "amount", label: "Amount" },
            { key: "dueDate", label: "Due Date", render: (r) => r?.formatted?.DueDateDateString || r?.dueDate || "—" },
          ]}
        />
      ),
      onClick: () => {},
    },
    {
      id: "transactions",
      label: "Transactions",
      content: (
        <OrgScopedList
          organizationId={organizationId}
          httpFetchDataFn={OrganizationService.getApiOrganizationTransactionOrganizationlist}
          emptyHeading="No transactions"
          columns={[
            { key: "payeeAccountName", label: "Payee Account" },
            { key: "description", label: "Description" },
            { key: "amount", label: "Amount" },
            { key: "paymentDate", label: "Date", render: (r) => r?.formatted?.PaymentDateDateString || r?.paymentDate || "—" },
            { key: "reference", label: "Reference" },
          ]}
        />
      ),
      onClick: () => {},
    },
  ];

  return (
    <div className="platform-portal-page">
      <RequestBreadcrumb
        firstName="Organizations"
        link={URLS.PLATFORM_ORGANIZATIONS}
        currentStep={org?.name || "Organization"}
        fetchData={() => {}}
      />
      {loading ? (
        <div className="platform-loadmore">
          <SimpleSpinner />
        </div>
      ) : (
        <DynamicTabs tabs={tabs} defaultActiveTab="overview" />
      )}
    </div>
  );
};

export default OrganizationDetail;
